import { useDispatch, useSelector } from "react-redux";
import { RootState } from "../store";
import {
  useLoginMutation,
  useGetUserProfileQuery,
  useUpdateProfileMutation,
  useDeleteAccountMutation,
  useLogoutMutation,
  LoginRequest,
  UpdateProfileRequest,
} from "../store/api/authAPI";
import { setToken, setUser, logout, clearToken } from "../store/slices/authSlice";
import { useCallback } from "react";

export const useAuth = () => {
  const dispatch = useDispatch();
  const { token, user, isAuthenticated } = useSelector(
    (state: RootState) => state.auth
  );

  // Login mutation
  const [loginMutation, { isLoading: isLoginLoading, error: loginError }] =
    useLoginMutation();

  // Get user profile query (disabled - using getAdminProfile from useUser hook instead)
  const {
    data: profileData,
    isLoading: isProfileLoading,
    error: profileError,
    refetch: refetchProfile,
  } = useGetUserProfileQuery(undefined, {
    skip: true, // Always skip - using getAdminProfile from useUser hook instead
  });

  // Update profile mutation
  const [updateProfileMutation, { isLoading: isUpdateLoading, error: updateError }] =
    useUpdateProfileMutation();

  // Delete account mutation
  const [deleteAccountMutation, { isLoading: isDeleteLoading, error: deleteError }] =
    useDeleteAccountMutation();

  // Logout mutation
  const [logoutMutation, { isLoading: isLogoutLoading, error: logoutError }] =
    useLogoutMutation();

  // Login function
  const login = useCallback(
    async (credentials: LoginRequest) => {
      try {
        const response = await loginMutation(credentials).unwrap();
        // Extract token and user from nested response structure
        const token = response?.data?.token || response?.token;
        const userData = response?.data;

        if (token) {
          dispatch(setToken(token));
        }
        if (userData) {
          // Remove token from user data before storing (token is stored separately)
          const { token: _, tokenType: __, password: ___, ...user } = userData;
          dispatch(setUser(user));
        }
        return response;
      } catch (error) {
        throw error;
      }
    },
    [loginMutation, dispatch]
  );

  // Update profile function
  const updateProfile = useCallback(
    async (data: UpdateProfileRequest) => {
      try {
        const response = await updateProfileMutation(data).unwrap();
        if (response) {
          dispatch(setUser(response));
        }
        return response;
      } catch (error) {
        throw error;
      }
    },
    [updateProfileMutation, dispatch]
  );

  // Delete account function
  const deleteAccount = useCallback(async () => {
    try {
      const response = await deleteAccountMutation().unwrap();
      dispatch(logout());
      return response;
    } catch (error) {
      throw error;
    }
  }, [deleteAccountMutation, dispatch]);

  // Logout function
  const handleLogout = useCallback(async () => {
    try {
      await logoutMutation().unwrap();
      dispatch(logout());
    } catch (error) {
      // Even if API call fails, logout locally
      dispatch(logout());
      throw error;
    }
  }, [logoutMutation, dispatch]);

  // Clear token function (for 404 errors)
  const handleClearToken = useCallback(() => {
    dispatch(clearToken());
  }, [dispatch]);

  return {
    // State
    token,
    user,
    isAuthenticated,
    profileData,

    // Loading states
    isLoginLoading,
    isProfileLoading,
    isUpdateLoading,
    isDeleteLoading,
    isLogoutLoading,

    // Error states
    loginError,
    profileError,
    updateError,
    deleteError,
    logoutError,

    // Actions
    login,
    updateProfile,
    deleteAccount,
    logout: handleLogout,
    clearToken: handleClearToken,
    refetchProfile,
  };
};

